import { useState, useEffect, useCallback } from 'react'
import { Modal, Spin } from 'antd'
import { useParams } from 'react-router-dom'
import type { StakeholderTemplate } from '@bandos/shared'
import { useStakeholderContext, useStakeholderTemplates } from '../../../../features/stakeholder'
import { UpgradePromptModal } from '../../../UpgradePromptModal/UpgradePromptModal'
import { usePortraitState, useCharacterForm, useTeamOperations } from './hooks'
import { RosterPanel } from './components/RosterPanel'
import { TeamPanel } from './components/TeamPanel'
import { ProfilePanel } from './components/ProfilePanel'
import type { CharacterSelectionModalProps, RightPanelView } from './CharacterSelectionModal.types'
import { ModalContainer, LoadingContainer } from './styles/CharacterSelectionModalStyles'

export function CharacterSelectionModal({
  isVisible,
  onClose,
  initialSelectedTemplateId,
}: CharacterSelectionModalProps) {
  const { id: mindmapId } = useParams<{ id: string }>()
  const {
    projectStakeholders,
    addStakeholder,
    removeStakeholder,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  } = useStakeholderContext()

  const { templates, loading, loadTemplates, markGeneratingPortrait, isGeneratingPortrait } =
    useStakeholderTemplates()

  const { brokenPortraits, markPortraitBroken } = usePortraitState()

  // Shared state lifted to parent to avoid circular hook dependencies
  const [selectedTemplate, setSelectedTemplate] = useState<StakeholderTemplate | null>(null)
  const [rightPanelView, setRightPanelView] = useState<RightPanelView>('empty')
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [upgradeFeatureName, setUpgradeFeatureName] = useState('More team members')

  const showUpgrade = useCallback((featureName: string) => {
    setUpgradeFeatureName(featureName)
    setShowUpgradeModal(true)
  }, [])

  const handleCardSelect = useCallback((template: StakeholderTemplate) => {
    setSelectedTemplate(template)
    setRightPanelView('profile')
  }, [])

  const teamOps = useTeamOperations({
    mindmapId,
    projectStakeholders,
    templates,
    addStakeholder,
    removeStakeholder,
    selectedTemplate,
    setSelectedTemplate,
    setRightPanelView,
  })

  const form = useCharacterForm({
    createTemplate,
    updateTemplate,
    deleteTemplate,
    loadTemplates,
    templates,
    markGeneratingPortrait,
    selectedTemplate,
    setSelectedTemplate,
    setRightPanelView,
  })

  // Refresh templates and reset state when modal opens
  useEffect(() => {
    if (isVisible) {
      void loadTemplates()
      if (!initialSelectedTemplateId) {
        setSelectedTemplate(null)
        setRightPanelView('empty')
      }
      form.resetForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible])

  // Handle initial template selection after templates load
  useEffect(() => {
    if (isVisible && initialSelectedTemplateId && templates.length > 0 && !loading) {
      const template = templates.find((t) => t.id === initialSelectedTemplateId)
      if (template) {
        setSelectedTemplate(template)
        setRightPanelView('profile')
      }
    }
  }, [isVisible, initialSelectedTemplateId, templates, loading])

  const personalTemplates = templates.filter((t) => !t.is_public)
  const prebuiltTemplates = templates.filter((t) => t.is_public)

  // Only show full loading screen on initial load
  if (loading && isVisible && templates.length === 0) {
    return (
      <Modal
        title="Select Your Team"
        open={isVisible}
        onCancel={onClose}
        footer={null}
        width="95vw"
        style={{ maxWidth: 1100 }}
        centered
        destroyOnHidden
      >
        <LoadingContainer>
          <Spin size="large" />
        </LoadingContainer>
      </Modal>
    )
  }

  return (
    <>
      <Modal
        title="Select Your Team"
        open={isVisible}
        onCancel={onClose}
        footer={null}
        width="95vw"
        style={{ maxWidth: 1100 }}
        centered
        destroyOnHidden
        styles={{
          body: {
            padding: 0,
            overflow: 'hidden',
          },
        }}
      >
        <ModalContainer>
          <RosterPanel
            templates={templates}
            personalTemplates={personalTemplates}
            prebuiltTemplates={prebuiltTemplates}
            selectedTemplate={selectedTemplate}
            isTeamFull={teamOps.isTeamFull}
            teamSize={projectStakeholders.length}
            operatingIds={teamOps.operatingIds}
            brokenPortraits={brokenPortraits}
            markPortraitBroken={markPortraitBroken}
            isTemplateInTeam={teamOps.isTemplateInTeam}
            isGeneratingPortrait={isGeneratingPortrait}
            onCardSelect={handleCardSelect}
            onEditClick={form.handleEditClick}
            onAddToTeam={teamOps.handleAddToTeam}
            onCreateNew={form.handleCreateNewClick}
          />
          <TeamPanel
            projectStakeholders={projectStakeholders}
            effectiveLimit={teamOps.effectiveLimit}
            operatingIds={teamOps.operatingIds}
            brokenPortraits={brokenPortraits}
            markPortraitBroken={markPortraitBroken}
            getTemplateForSlot={teamOps.getTemplateForSlot}
            isGeneratingPortrait={isGeneratingPortrait}
            onCardSelect={handleCardSelect}
            onSlotRemove={teamOps.handleSlotRemove}
            onShowUpgrade={() => showUpgrade('More team members')}
          />
          <ProfilePanel
            rightPanelView={rightPanelView}
            displayTemplate={selectedTemplate}
            editingTemplate={form.editingTemplate}
            formData={form.formData}
            submitting={form.submitting}
            teamSize={projectStakeholders.length}
            operatingIds={teamOps.operatingIds}
            brokenPortraits={brokenPortraits}
            markPortraitBroken={markPortraitBroken}
            isTemplateInTeam={teamOps.isTemplateInTeam}
            isGeneratingPortrait={isGeneratingPortrait}
            updateFormField={form.updateFormField}
            addArrayItem={form.addArrayItem}
            removeArrayItem={form.removeArrayItem}
            updateArrayItem={form.updateArrayItem}
            onEditClick={form.handleEditClick}
            onAddToTeam={teamOps.handleAddToTeam}
            onDeleteTemplate={form.handleDeleteTemplate}
            onFormCancel={form.handleFormCancel}
            onCreateSubmit={() => void form.handleCreateSubmit()}
            onEditSave={() => void form.handleEditSave()}
            onSaveAsNew={() => void form.handleSaveAsNew()}
          />
        </ModalContainer>
      </Modal>

      <UpgradePromptModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        featureName={upgradeFeatureName}
      />
    </>
  )
}
